import { Component } from 'preact';
import { route } from 'preact-router';
import util from 'preact-util';

import jsQR from 'jsqr';
import { BrowserQRCodeReader, BrowserMultiFormatReader } from '@zxing/browser';

/*
 * CameraComponent - Advanced camera component with QR code and barcode scanning
 *
 * Props:
 * - scanQRCodes: boolean (default: true) - Enable QR code scanning
 * - scanBarcodes: boolean (default: false) - Enable barcode scanning (Code128, EAN, UPC, etc.)
 * - callback: function - Called when a code is detected with { data, format }
 * - handleUpload: function - Called when image is captured
 * - closeCamera: function - Called when camera should be closed
 * - showCapture: boolean (default: true) - Show capture button
 * - showCameraToggle: boolean (default: true) - Show camera switch button
 * - useHighResolution: boolean (default: false) - Use 1920x1080 for video stream (better captures, slower scanning)
 * - followUrls: boolean (default: true) - When false, http(s) payloads are passed to callback instead of being navigated to
 */
class CameraComponent extends Component {
    constructor() {
        super();
        this.state = {
            hasPermission: false,    // Track camera permission
            stream: null,            // Store the media stream
            orientation: 'portrait', // Initialize to portrait mode
            cameras: [],             // Available cameras
            cameraIndex: 0,          // Selected camera index
        };
        this.videoRef = null;  // Reference to the video element
        this.canvasRef = null;

        this.fps = 10;  // Frames per second
        this.isCapturing = false;
        this.mediaRecorder = null;
        this.mediaChunks = []; // Store video and audio data chunks

        // QR Code scanning setup
        this.barcodeDetector = null;
        this.zxingReader = null;
        this.scanningMethod = null; // 'native', 'zxing', or 'jsqr'
    }

    // Initialize barcode scanning capabilities
    initializeBarcodeScanning = async () => {
        const { scanBarcodes = false, scanQRCodes = true } = this.props;

        // Determine what formats to scan for
        const formats = [];
        if (scanQRCodes) formats.push('qr_code');
        if (scanBarcodes) {
            formats.push(...[
                'code_128', 'code_39', 'code_93', 'codabar',
                'ean_13', 'ean_8', 'itf', 'upc_a', 'upc_e',
                'data_matrix', 'pdf417', 'aztec'
            ]);
        }

        // Try native BarcodeDetector first (Chrome, Edge, Samsung Internet)
        if ('BarcodeDetector' in window && formats.length > 0) {
            try {
                const barcodeDetector = new BarcodeDetector({ formats });
                // Test if it actually works
                const testCanvas = document.createElement('canvas');
                testCanvas.width = testCanvas.height = 1;
                const testImageData = testCanvas.getContext('2d').getImageData(0, 0, 1, 1);
                await barcodeDetector.detect(testImageData);

                this.barcodeDetector = barcodeDetector;
                this.scanningMethod = 'native';
                console.log('Using native BarcodeDetector API for:', formats.join(', '));
                return;
            } catch (error) {
                console.warn('BarcodeDetector available but not working:', error);
            }
        }

        // Fallback to @zxing/browser
        try {
            // ZXing supports multiple formats
            if (scanBarcodes && !scanQRCodes) {
                // For barcode-only scanning, use the multi-format reader
                this.zxingReader = new BrowserMultiFormatReader();
            } else {
                // For QR codes (and mixed scanning), use QR reader which also supports some other formats
                this.zxingReader = new BrowserQRCodeReader();
            }
            this.scanningMethod = 'zxing';
            console.log('Using @zxing/browser for scanning');
            return;
        } catch (error) {
            console.warn('ZXing initialization failed:', error);
        }

        // Last resort: jsQR (QR codes only)
        if (scanQRCodes) {
            this.scanningMethod = 'jsqr';
            console.log('Using jsQR as fallback for QR scanning');
        }
    }

    // Request permission and enumerate devices
    // Request permission to access the camera
    requestPermissionAndStartCamera = async () => {
        try {
            // First try to get basic camera access to trigger permission prompt
            let initialStream = null;
            try {
                // Try back camera first on mobile
                initialStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });
            } catch (err) {
                // Fallback to any camera
                initialStream = await navigator.mediaDevices.getUserMedia({ video: true });
            }

            // Now enumerate devices (should have labels after permission granted)
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');

            let preferredDeviceId = null;
            let deviceIds = [];

            if (videoDevices.length > 0) {
                // Prefer back camera (environment) over front camera (user)
                const backCamera = videoDevices.find(device =>
                    device.label.toLowerCase().includes('back') ||
                    device.label.toLowerCase().includes('rear') ||
                    device.label.toLowerCase().includes('environment')
                );

                const frontCamera = videoDevices.find(device =>
                    device.label.toLowerCase().includes('front') ||
                    device.label.toLowerCase().includes('user') ||
                    device.label.toLowerCase().includes('selfie')
                );

                // Build device list with back camera first
                if (backCamera) {
                    deviceIds.push(backCamera.deviceId);
                    preferredDeviceId = backCamera.deviceId;
                }
                if (frontCamera && frontCamera.deviceId !== preferredDeviceId) {
                    deviceIds.push(frontCamera.deviceId);
                }

                // Add any other cameras
                videoDevices.forEach(device => {
                    if (!deviceIds.includes(device.deviceId)) {
                        deviceIds.push(device.deviceId);
                    }
                });

                // If no back camera found, use first available
                if (!preferredDeviceId) {
                    preferredDeviceId = deviceIds[0];
                }
            }

            // Try to get user's previous choice
            const savedDeviceId = util.get('selectedDeviceId');
            if (savedDeviceId && deviceIds.includes(savedDeviceId)) {
                preferredDeviceId = savedDeviceId;
            }

            // Check if our initial stream is already using the preferred camera
            const initialDeviceId = initialStream?.getVideoTracks()[0]?.getSettings()?.deviceId;
            let stream = initialStream;

            // Switch to preferred camera if it's different from what we got initially
            if (preferredDeviceId && preferredDeviceId !== initialDeviceId) {
                // Stop initial stream
                if (initialStream) {
                    initialStream.getTracks().forEach(track => track.stop());
                }

                // Request access to preferred camera
                const constraints = {
                    video: { deviceId: { exact: preferredDeviceId } }
                };
                stream = await navigator.mediaDevices.getUserMedia(constraints);
            }

            // Set the stream to the video element
            if (this.videoRef) {
                this.videoRef.srcObject = stream;
                this.videoRef.muted = true; // Mute the video for feedback-free operation
            }

            // Store the stream and camera info
            this.setState({
                hasPermission: true,
                stream: stream,
                cameras: videoDevices,
                deviceIds,
                cameraIndex: deviceIds.indexOf(preferredDeviceId) || 0
            }, () => {
                // Start QR code scanning with the current stream
                this.startScanning();
            });

        } catch (err) {
            console.error("Error accessing the camera:", err);
            this.setState({ hasPermission: false });
        }
    }

    // Function to switch to the next available camera
    changeCamera = () => {
        const { cameras, deviceIds, cameraIndex, stream } = this.state;

        // If more than one camera exists, switch to the next one
        if (deviceIds.length > 1) {
            const nextCameraIndex = (cameraIndex + 1) % deviceIds.length;
            const selectedDeviceId = deviceIds[nextCameraIndex];

            if (selectedDeviceId) {
                // Stop current scanning
                this.stopScanning();

                // Stop the current stream
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                }

                // Save the user's choice
                util.set('selectedDeviceId', selectedDeviceId);

                // Update the state with the new camera index
                this.setState({
                    cameraIndex: nextCameraIndex,
                }, () => {
                    // Start the camera with the new device
                    this.startCamera(selectedDeviceId);
                });
            } else {
                console.warn('No valid device ID found for camera index:', nextCameraIndex);
            }
        }
    }

    startCamera = async (deviceId) => {
        const { cameras, deviceIds, cameraIndex, stream } = this.state;
        const { useHighResolution = false } = this.props;

        try {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }

            // Build video constraints based on useHighResolution prop
            const videoConstraints = useHighResolution ? {
                width: { ideal: 2560 },  // 2K (QHD) for high-quality captures
                height: { ideal: 1440 }
            } : {
                width: { ideal: 1920 },  // Full HD for scanning
                height: { ideal: 1080 }
            };

            // Only add deviceId constraint if it's valid
            if (deviceId && typeof deviceId === 'string' && deviceId.length > 0) {
                videoConstraints.deviceId = { exact: deviceId };
            }

            // Request access to the camera
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: videoConstraints,
                audio: false,
            });

            // Set the stream to the video element
            if (this.videoRef) {
                this.videoRef.srcObject = newStream;
                this.videoRef.muted = true;  // Mute video for feedback-free operation
            }

            // Mirror the video if the front camera (selfie camera) is being used
            // console.log(newStream.getVideoTracks()[0].getSettings());
            const facingMode = newStream.getVideoTracks()[0].getSettings().facingMode;
            if (facingMode === 'user') {
                // Apply mirroring (for selfie/front camera)
                this.videoRef.style.transform = 'scaleX(-1)';
            } else {
                // Reset the transform if it's not a front-facing camera
                this.videoRef.style.transform = 'none';
                // this.videoRef.style.transform = 'scaleX(-1)';

            }

            // Update the state with the stream and permission granted
            this.setState({
                hasPermission: true,
                stream: newStream,
            });
            this.startScanning();  // Start scanning for QR codes

        } catch (err) {
            console.error("Error accessing the camera:", err);
            this.setState({ hasPermission: false });
        }
    }

    startScanning = async () => {
        const { callback = () => {} } = this.props;

        // Initialize barcode scanning if not already done
        if (!this.scanningMethod) {
            await this.initializeBarcodeScanning();
        }

        // Set up scanning based on available method
        if (this.scanningMethod === 'native') {
            this.startNativeScanning(callback);
        } else if (this.scanningMethod === 'zxing') {
            this.startZXingScanning(callback);
        } else {
            this.startJsQRScanning(callback);
        }
    }

    // Native BarcodeDetector scanning
    startNativeScanning = (callback) => {
        const scanInterval = setInterval(async () => {
            if (this.videoRef && this.barcodeDetector) {
                try {
                    const barcodes = await this.barcodeDetector.detect(this.videoRef);
                    if (barcodes.length > 0) {
                        const detectedCode = barcodes[0];
                        this.handleBarcodeDetected(detectedCode.rawValue, detectedCode.format, callback);
                    }
                } catch (error) {
                    console.error('Native barcode detection error:', error);
                    // Fallback to ZXing on error
                    clearInterval(scanInterval);
                    this.scanningMethod = 'zxing';
                    this.startZXingScanning(callback);
                }
            }
        }, 500); // Faster scanning with native API

        this._scanInterval = scanInterval;
    }

    // ZXing scanning
    startZXingScanning = (callback) => {
        const scanInterval = setInterval(async () => {
            if (this.videoRef && this.canvasRef && this.zxingReader) {
                try {
                    const canvas = this.canvasRef;
                    const context = canvas.getContext('2d');

                    // Ensure the canvas size matches the video stream
                    canvas.width = this.videoRef.videoWidth;
                    canvas.height = this.videoRef.videoHeight;

                    if (canvas.width === 0 || canvas.height === 0) {
                        return;
                    }

                    // Draw the current video frame to the canvas
                    context.drawImage(this.videoRef, 0, 0, canvas.width, canvas.height);

                    // Use ZXing to decode from canvas
                    const result = await this.zxingReader.decodeFromCanvas(canvas);
                    if (result) {
                        const format = result.getBarcodeFormat ? result.getBarcodeFormat().toString() : 'unknown';
                        this.handleBarcodeDetected(result.getText(), format, callback);
                    }
                } catch (error) {
                    // ZXing throws NotFoundException when no QR code is found, this is normal
                    if (error.name !== 'NotFoundException' &&
                        !error.message?.includes('No MultiFormat Readers were able to detect the code')) {
                        console.warn('ZXing scanning error:', error);
                    }
                }
            }
        }, 750);

        this._scanInterval = scanInterval;
    }

    // Fallback jsQR scanning (unchanged from original)
    startJsQRScanning = (callback) => {
        const scanInterval = setInterval(() => {
            if (this.videoRef && this.canvasRef) {
                const canvas = this.canvasRef;
                const context = canvas.getContext('2d');

                canvas.width = this.videoRef.videoWidth;
                canvas.height = this.videoRef.videoHeight;

                if (canvas.width === 0 || canvas.height === 0) {
                    return;
                }

                context.drawImage(this.videoRef, 0, 0, canvas.width, canvas.height);
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, canvas.width, canvas.height);

                if (code) {
                    this.handleBarcodeDetected(code.data, 'QR_CODE', callback);
                }
            }
        }, 750);

        this._scanInterval = scanInterval;
    }

    // Handle barcode/QR code detection (common logic)
    handleBarcodeDetected = (codeData, format, callback) => {
        console.log('Code detected:', { data: codeData, format });

        if (this.props.followUrls === false) {
            this.setState({ qrCode: codeData });
            callback({ data: codeData, format });
            return;
        }

        // Check if it's a URL (common in QR codes)
        const isUrl = codeData.startsWith('http://') || codeData.startsWith('https://');

        if (isUrl) {
            try {
                const url = new URL(codeData);
                console.log('URL:', url);
                this.setState({ qrCode: codeData });
                if (url.hash) {
                    const gotoPage = url.hash.replace('#', '');
                    console.log('gotoPage:', gotoPage);
                    route(gotoPage);
                }
            } catch (error) {
                console.error('Invalid URL in code:', error);
                this.setState({ qrCode: codeData });
                callback({ data: codeData, format });
            }
        } else {
            this.setState({ qrCode: codeData });
            callback({ data: codeData, format });
        }
    }

    stopScanning = () => {
        if (this._scanInterval) {
            clearInterval(this._scanInterval);
        }
    }

    captureImage = async () => {
        const { handleUpload } = this.props;

        // Try using ImageCapture API for high-resolution photos
        if ('ImageCapture' in window && this.videoRef && this.videoRef.srcObject) {
            try {
                const track = this.videoRef.srcObject.getVideoTracks()[0];
                const imageCapture = new ImageCapture(track);

                // Get camera capabilities to log max resolution
                const capabilities = track.getCapabilities();
                console.log('Camera capabilities:', capabilities);

                // Capture at maximum resolution supported by camera
                const photoBlob = await imageCapture.takePhoto();
                console.log('Photo captured:', photoBlob.size, 'bytes');

                const filename = `captured_image-${util.isoDate(new Date, false, false, true)}.jpg`;
                const fileObject = new File([photoBlob], filename, { type: 'image/jpeg' });

                handleUpload(fileObject);
                return;
            } catch (error) {
                console.warn('ImageCapture API failed, falling back to canvas method:', error);
            }
        }

        // Fallback: Capture the current frame from the video element using canvas
        const canvas = this.canvasRef;
        const context = canvas.getContext('2d');

        // Get the intrinsic video dimensions (video resolution)
        const videoWidth = this.videoRef.videoWidth;
        const videoHeight = this.videoRef.videoHeight;

        // Get the visible size of the video element (client size)
        const videoClientWidth = this.videoRef.clientWidth;
        const videoClientHeight = this.videoRef.clientHeight;

        // Calculate the aspect ratio of the video
        const videoAspectRatio = videoWidth / videoHeight;
        const clientAspectRatio = videoClientWidth / videoClientHeight;

        // Calculate the offsets for cropping based on object-fit: cover
        let scale = 1;
        let offsetX = 0;
        let offsetY = 0;

        if (videoAspectRatio > clientAspectRatio) {
            // If the video is wider (landscape video, portrait container)
            scale = videoClientHeight / videoHeight;
        } else {
            // If the video is taller (portrait video, landscape container)
            scale = videoClientWidth / videoWidth;
        }

        // If the container is taller (portrait video, landscape container)
        offsetX = Math.floor((videoWidth / 2) - (videoClientWidth / 2) / scale); // Center the video horizontally
        offsetY = Math.floor((videoHeight / 2) - (videoClientHeight / 2) / scale); // Center the video vertically

        let cropWidth = Math.floor(videoClientWidth / scale);
        let cropHeight = Math.floor(videoClientHeight / scale);

        // Set canvas dimensions based on the calculated width and height
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Draw only the visible portion of the video (cropped area)
        context.drawImage(
            this.videoRef,  // Video element
            offsetX,        // Horizontal offset for cropping
            offsetY,        // Vertical offset for cropping
            cropWidth,      // Width to draw on the canvas
            cropHeight,     // Height to draw on the canvas
            0,              // Destination: start from the top-left corner
            0,              // Destination: start from the top-left corner
            cropWidth,      // Destination: cropWidth,
            cropHeight,     // Destination: cropHeight
        );

        // Convert the canvas content to a data URL (Base64 encoded image)
        const imageData = canvas.toDataURL('image/jpeg', 0.95);

        // Convert the Base64 image data to a Blob object
        const byteString = atob(imageData.split(',')[1]);  // Remove the data URL prefix and decode the Base64 string
        const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];  // Get the MIME type (image/png)
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uintArray = new Uint8Array(arrayBuffer);

        // Fill the array buffer with the byte values
        for (let i = 0; i < byteString.length; i++) {
            uintArray[i] = byteString.charCodeAt(i);
        }

        // Create a Blob object from the byte array
        const fileBlob = new Blob([uintArray], { type: mimeString });

        // Create a File object to simulate the uploaded image
        const filename = `captured_image-${util.isoDate(new Date, false, false, true)}.jpg`;
        const fileObject = new File([fileBlob], filename, { type: mimeString });

        // Call handleUpload to upload the image
        handleUpload(fileObject);
    }

    handleOrientationChange = () => {
        // Update the orientation state when the orientation changes
        this.updateOrientation();
    }

    updateOrientation = () => {
        // Check orientation using matchMedia or window.orientation (depending on browser support)
        const orientation = window.matchMedia("(orientation: landscape)").matches ? 'landscape' : 'portrait';
        this.setState({ orientation });
    }

    // Video and Audio Capture
    startVideoCapture = async () => {
        const { fps: inputFps, useHighResolution = false } = this.props;
        if (this.isCapturing) return; // Don't start if already capturing

        this.isCapturing = true;

        // Build video constraints based on useHighResolution prop
        const videoConstraints = useHighResolution ? {
            width: { ideal: 2560 },
            height: { ideal: 1440 },
            frameRate: { ideal: this.fps, max: this.fps }
        } : {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: this.fps, max: this.fps }
        };

        // Get video and audio stream
        const stream  = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: true,
        });

        // Attach video stream to video element
        this.videoRef.srcObject = stream;

        // Create MediaRecorder with the video/audio stream
        this.mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });

        // Collect the video/audio data
        this.mediaRecorder.ondataavailable = (event) => {
            this.mediaChunks.push(event.data); // Push media chunks to array
        };

        // When recording stops, we create the Blob and File
        this.mediaRecorder.onstop = () => {
            this.createVideoFromChunks();
        };

        // Start recording
        this.mediaRecorder.start();
        console.log('Recording started');
    };

    // Stop capturing after some time or when the user chooses to stop
    stopVideoCapture = () => {
        if (!this.isCapturing) return; // If not capturing, do nothing

        this.mediaRecorder.stop(); // Stop recording
        this.isCapturing = false;
        console.log('Recording stopped');
    };

    // Create a video from the recorded chunks (audio + video)
    createVideoFromChunks = () => {
        const videoBlob = new Blob(this.mediaChunks, { type: 'video/webm' });

        // Convert Blob to File object to simulate video upload
        const filename = `captured_video_${new Date().toISOString()}.webm`;
        const fileObject = new File([videoBlob], filename, { type: 'video/webm' });

        // Call handleUpload to upload the video file
        const { handleUpload } = this.props;
        handleUpload(fileObject);

        console.log('Video created and ready for upload');
    };

    componentDidMount() {
        this.requestPermissionAndStartCamera();
        // this.startCamera();

        window.addEventListener('orientationchange', this.handleOrientationChange); // Listen for orientation change
        this.updateOrientation(); // Set the initial orientation
    }

    componentWillUnmount() {
        // Stop QR code scanning
        this.stopScanning();

        // Clean up ZXing reader
        if (this.zxingReader) {
            this.zxingReader = null;
        }

        if (this.state.stream) {
            // Stop the camera stream when the component is unmounted
            const tracks = this.state.stream.getTracks();
            tracks.forEach(track => track.stop());
        }
        window.removeEventListener('orientationchange', this.handleOrientationChange); // Cleanup
    }

    render() {
        const { orientation } = this.state;
        const {
            handleUpload = () => {},
            closeCamera = () => {},
            videoEnabled,
            showCapture = true,
            showCameraToggle = true,
        } = this.props;
        return (
            <div class='w-100 h-100 overflow-hidden bg-darkmode rounded-lg mx-1 position-relative'>
                {/* {orientation === 'portrait' ? (
                    <p>Portrait Mode</p>
                ) : (
                    <p>Landscape Mode</p>
                )} */}
                {/* {videoEnabled ? <>
                    <span class='text-white'>videoEnabled</span>
                </> : <>
                    <span class='text-white'>videoEnabled not enabled</span>
                </>} */}
                <video
                    ref={el => { this.videoRef = el; }}
                    autoPlay
                    playsInline
                    muted
                    class='w-100 h-100 rounded-lg'
                    height="auto"
                    style={`
                        display: ${this.state.hasPermission ? 'block' : 'none'};
                        object-fit: cover;
                        transition: transform 0.5s ease;
                    `}  // Hide until permission is granted
                ></video>

                {showCapture && handleUpload && <>
                    <button
                        class='btn btn-lg btn-danger position-absolute rounded-pill px-4'
                        style={`
                            bottom: 2rem;
                            left: 50%;
                            transform: translateX(-50%);
                            font-size: 2.5rem;
                        `}
                        onClick={this.captureImage}
                    >
                        <i class='fa-solid fa-camera' />
                    </button>
                </>}

                {showCameraToggle && <>
                    {this.state.deviceIds?.length > 1 && (
                        <button
                            class='btn btn-lg btn-secondary position-absolute rounded-pill'
                            style={`
                                bottom: 2rem;
                                left: 75%;
                            `}
                            onClick={this.changeCamera}
                            title={`Switch camera (${this.state.cameraIndex + 1}/${this.state.deviceIds?.length})`}
                        >
                            <i class='fa-solid fa-camera-rotate' />
                        </button>
                    )}

                    {/* <button
                        class='btn btn-lg btn-secondary position-absolute rounded-pill'
                        style={{ top: '1rem', left: '1rem' }}
                        onClick={closeCamera}
                    >
                        <i class='fa-solid fa-times' />
                    </button> */}

                </>}

                {/* Hidden canvas for processing video frames */}
                <canvas ref={el => { this.canvasRef = el; }} style={{ display: 'none' }}></canvas>

                {/* Display the QR code data if available */}
                {/* {this.state.qrCode && (
                    <div>
                        <p>QR Code Data: {this.state.qrCode}</p>
                    </div>
                )} */}

                {!this.state.hasPermission && (
                    <p>Camera access denied or not available.</p>
                )}
            </div>
        );
    }
}

export default CameraComponent;